import { useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatusPill } from "@/components/shared/StatusPill";
import { BusinessStage } from "@/components/shared/BusinessStage";
import { AICard, AIBadge } from "@/components/shared/AICard";
import { MilestoneTimeline } from "@/components/shared/MilestoneTimeline";
import { RouteTracker } from "@/components/shared/RouteTracker";
import { DocumentPanel } from "@/components/shared/DocumentPanel";
import { useFileSelectConfirm, FileSelectConfirmBar } from "@/components/shared/FileSelectConfirm";
import OpsBookingPanel from "@/components/shared/OpsBookingPanel";
import ExecutionPanel from "@/components/shared/ExecutionPanel";
import CustomsPanel from "@/components/shared/CustomsPanel";
import { Loading } from "@/components/shared/Loading";
import { HandoffConfirmDialog } from "@/components/shared/HandoffConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useJob, useJobDocuments, useRequiredDocs, useOpsNextAction, useRole, useCommunications, useOpsFinanceHandoffReadiness, useOpsActualCostReadiness, useOpsActualCosts, useCustomerDocumentChecklist } from "@/api/hooks";
import { money } from "@/lib/format";
import { useApp } from "@/context/AppContext";
import { apiErrorMessage } from "@/lib/apiError";
import { ArrowLeft, FileText, AlertTriangle, Phone, MessageCircle, Mail, Plane, MapPin, Package, Lock, Upload, Loader2, Sparkles, Check, Archive, Truck } from "lucide-react";

// Item 2 (Aug 2026 Operations round) — mirrors backend OPS_COST_CATEGORIES
// (server.py) exactly. Fixed, uniform list (the user's own example),
// applied regardless of job mode — see server.py comment for why this is a
// deliberate judgment call rather than derived from an existing list.
const OPS_COST_CATEGORIES = ["Freight", "Origin Handling", "Destination Handling", "Customs", "Delivery"];

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const role = useRole();
  const isOperations = role === "operations";
  const { openComposer } = useApp();
  const qc = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [uploadingCostEvidence, setUploadingCostEvidence] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [docOpen, setDocOpen] = useState(false);
  const [activeDoc, setActiveDoc] = useState(null);
  // Item 2 (Aug 2026 Operations round) — manual actual-cost entry (a
  // free-text Vendor/Invoice/Amount/Expected form disconnected from any
  // uploaded document) has been REMOVED. Actual cost is now derived from
  // real cost/booking evidence documents Operations uploads, each tagged
  // with a category + the real amount read off that document. This state
  // is for the "Send Actual Cost to Finance" review-ONLY confirmation
  // dialog (aggregated server-side, no editable fields — see
  // useOpsActualCostReadiness below).
  const [costHandoffOpen, setCostHandoffOpen] = useState(false);
  const [costHandoffBusy, setCostHandoffBusy] = useState(false);
  // Item 2 — the ONLY fields Operations fills in per cost-document upload:
  // which cost category this document represents, and the real amount read
  // off it. Cleared after each successful submit / cancel / re-upload.
  const [costEvidenceCategory, setCostEvidenceCategory] = useState("");
  const [costEvidenceAmount, setCostEvidenceAmount] = useState("");
  // Aug 2026 item 3 — Operations can tag an ADDITIONAL trade document as
  // needed (beyond the mandatory default set), directly against the Trade's
  // own inquiry-scoped checklist (see POST
  // /commercial/inquiries/{iid}/trade-documents) — the same checklist Sales
  // sees on Customer360/Workspace, so a document Operations spots mid-
  // execution shows up there too instead of living only on this page.
  const [extraDocName, setExtraDocName] = useState("");
  const [extraDocBusy, setExtraDocBusy] = useState(false);
  // Bug fix — the per-row "Upload" button in the Trade Documents list (on
  // the Milestones tab) triggers `tradeDocConfirm`'s hidden file input, but
  // that input previously lived inside the separate Documents tab's markup.
  // Radix Tabs only mounts the ACTIVE tab's content, so clicking Upload
  // while on Milestones tried to click an input that didn't exist in the
  // DOM — a silent no-op, reported as "the button doesn't work". The input
  // is now mounted once, outside any tab panel (see below `<Tabs>`), so it
  // always exists regardless of which tab is active. `uploadTargetDocName`
  // additionally tracks WHICH required document a click came from, so the
  // upload gets tagged with the correct docType (previously any per-row
  // upload was mis-tagged by a filename guess in `onUpload`, so it never
  // matched back to the row that triggered it).
  const [uploadTargetDocName, setUploadTargetDocName] = useState(null);
  // Item 8 (Aug 2026 Operations round) — Operations can select ONE or
  // MULTIPLE missing trade documents and send exactly ONE combined "Ask
  // Customer" request, instead of one Composer session per document.
  const [selectedMissingDocs, setSelectedMissingDocs] = useState([]);
  // Items 7/10 (Aug 2026 Operations round) — final Operations -> Finance
  // handoff, gated by GET .../finance-handoff-readiness and confirmed via
  // the same HandoffConfirmDialog double-confirmation pattern as item 7's
  // actual-cost handoff below.
  const [financeHandoffOpen, setFinanceHandoffOpen] = useState(false);
  const [financeHandoffBusy, setFinanceHandoffBusy] = useState(false);
  // Item 1 — these two useFileSelectConfirm() calls must run on every
  // render (rules of hooks), so they're declared above the `if (!j) return`
  // early return below. The wrapper arrows reference onUpload/
  // onUploadCostEvidence, which are declared further down with `const` —
  // safe because these wrappers are only ever invoked later (on Submit),
  // by which time the render that defines them has already completed.
  // eslint-disable-next-line no-use-before-define
  const tradeDocConfirm = useFileSelectConfirm((file) => onUpload(file));
  // eslint-disable-next-line no-use-before-define
  const costEvidenceConfirm = useFileSelectConfirm((file) => onUploadCostEvidence(file));

  const { data: j } = useJob(id);
  const { data: jobDocs } = useJobDocuments(id);
  const { data: reqDocs } = useRequiredDocs(id);
  const { data: nextAction } = useOpsNextAction(id, isOperations);
  const { data: comm } = useCommunications(j?.customerId);
  const { data: handoffReadiness } = useOpsFinanceHandoffReadiness(id, isOperations);
  // Item 2 — real, server-aggregated per-category cost-document totals (no
  // client-side computation of "actual cost" from typed numbers).
  const { data: costReadiness } = useOpsActualCostReadiness(id, isOperations);
  const { data: actualCosts } = useOpsActualCosts(id, isOperations);
  // Item 3 — the SAME KYC/Trade document checklist Sales/Finance already see
  // on Customer360 (GET /customers/{cid}/document-checklist), read-only here.
  const { data: custChecklist } = useCustomerDocumentChecklist(j?.customerId);
  if (!j) return <Loading />;

  const parties = (j.parties || []).map((p) => `${p.role} · ${p.name}`);
  const openDoc = (d) => { setActiveDoc(d); setDocOpen(true); };
  const backPath = isOperations ? "/operations/jobs" : (j.customerId ? `/${role}/customers/${j.customerId}` : `/${role}/inbox`);
  const canArchive = role === "management" && ["FINANCIALLY_CLOSED", "CLOSED"].includes(String(j.status || "").toUpperCase());

  const archiveJob = async () => {
    if (!canArchive || !window.confirm(`Archive job ${j.id}? This creates its final archive manifest and cannot be undone from the dashboard.`)) return;
    setArchiving(true);
    try {
      await api.post(`/management/jobs/${j.id}/archive`, { expectedVersion: Number(j.version || 0) });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["job", j.id] }),
        qc.invalidateQueries({ queryKey: ["actions"] }),
      ]);
      toast.success(`Job ${j.id} archived`);
    } catch (err) {
      const detail = err.response?.data?.message || err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Job could not be archived");
    } finally {
      setArchiving(false);
    }
  };

  const onUpload = async (file) => {
    if (!isOperations || !file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("jobId", j.id);
    // Bug fix: a per-row "Upload" click (from a specific required document
    // in the Trade Documents list) now tags the upload with THAT document's
    // real name, so it correctly matches back to the row and flips it to
    // "Submitted" — previously every upload was tagged by guessing from the
    // filename, so a row-targeted upload never actually satisfied that row.
    fd.append("docType", uploadTargetDocName || (file.name.toLowerCase().includes("invoice") ? "Commercial Invoice" : "Document"));
    fd.append("customerId", j.customerId || "");
    fd.append("category", "TRADE");
    try {
      const { data } = await api.post("/documents/upload", fd);
      // Bug fix — this only invalidated ["docs"], but the Trade Documents
      // checklist above reads from useRequiredDocs (query key
      // ["reqdocs", jobId]) and the "Final Handoff to Finance" blockers read
      // from useOpsFinanceHandoffReadiness (["ops-finance-handoff", jobId]).
      // Neither was being told to refetch, so a real, correctly-saved
      // upload kept showing "Pending" / stale blockers until a manual page
      // reload forced a fresh fetch.
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["docs"] }),
        qc.invalidateQueries({ queryKey: ["reqdocs", j.id] }),
        qc.invalidateQueries({ queryKey: ["ops-finance-handoff", j.id] }),
        qc.invalidateQueries({ queryKey: ["cust-doc-checklist", j.customerId] }),
      ]);
      toast.success(data.aiRead ? "Document uploaded and analyzed — human review required" : "Document uploaded — manual review required");
      openDoc(data);
    } catch (err) {
      toast.error("Upload failed");
      throw err;
    } finally { setUploading(false); setUploadTargetDocName(null); }
  };

  // Item 2 (Aug 2026 Operations round) — documents Operations uploads AS
  // EVIDENCE of actual cost (vendor invoice, booking confirmation) remain a
  // structurally distinct category from customer-provided trade documents.
  // What changed: this upload now ALSO captures which cost category the
  // document represents and the real amount read off it (costEvidenceCategory
  // /costEvidenceAmount, filled in via the inline form that appears once a
  // file is staged — see FileSelectConfirmBar usage below), and immediately
  // creates the matching real supplier_invoices record via the existing
  // POST /operations/jobs/{jid}/actual-costs endpoint. No free-text vendor/
  // invoice number is collected from Operations — the backend derives those
  // from the uploaded document itself. There is no OCR/extraction in this
  // project, so the amount is exactly what Operations reads off the real
  // document and types in — never a fabricated/auto-filled number.
  const onUploadCostEvidence = async (file) => {
    if (!isOperations || !file) return;
    if (!costEvidenceCategory) { toast.error("Select which cost category this document is for"); throw new Error("category required"); }
    const amountNum = Number(costEvidenceAmount);
    if (!costEvidenceAmount || !(amountNum > 0)) { toast.error("Enter the real amount shown on this document"); throw new Error("amount required"); }
    setUploadingCostEvidence(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("jobId", j.id);
    fd.append("docType", "Vendor Invoice");
    fd.append("customerId", j.customerId || "");
    fd.append("category", "COST_EVIDENCE");
    try {
      const { data } = await api.post("/documents/upload", fd);
      await api.post(`/operations/jobs/${j.id}/actual-costs`, {
        category: costEvidenceCategory, amount: amountNum, currency: j.currency || "AED", documentIds: [data.id],
      });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["docs"] }),
        qc.invalidateQueries({ queryKey: ["ap"] }),
        qc.invalidateQueries({ queryKey: ["ops-actual-cost-readiness"] }),
      ]);
      toast.success(`${costEvidenceCategory} cost recorded from this document — visible to Finance now`);
      openDoc(data);
      setCostEvidenceCategory(""); setCostEvidenceAmount("");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Upload failed"));
      throw err;
    } finally { setUploadingCostEvidence(false); }
  };

  // Item 2 — "Send Actual Cost to Finance" is now review-ONLY: every field
  // shown comes from useOpsActualCostReadiness (server-aggregated real
  // supplier_invoices, one per uploaded cost document). Nothing is editable
  // here; confirming just records the explicit handoff moment.
  const submitCostHandoff = async () => {
    setCostHandoffBusy(true);
    try {
      await api.post(`/operations/jobs/${j.id}/actual-costs/handoff`, { expectedVersion: Number(j.version || 0) });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["ops-actual-cost-readiness"] }),
        qc.invalidateQueries({ queryKey: ["ap"] }),
      ]);
      toast.success("Actual cost handed off to Finance for cost review");
      setCostHandoffOpen(false);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not hand off actual costs"));
    } finally { setCostHandoffBusy(false); }
  };

  const requestDocumentFromCustomer = (label) => openComposer({
    channel: "whatsapp", parties, defaultParty: parties[0], customerId: j.customerId,
    subjectHint: `[${j.id}] Document requested`,
    intent: `ask the customer to provide ${label} for job ${j.id}`,
  });
  // Item 8 — one or more missing trade documents, selected via checkbox,
  // become exactly ONE combined Composer session (never one message per
  // document). Reuses the same Composer used everywhere else in this app
  // (channel picker there already covers WhatsApp/Email/Phone).
  const toggleMissingDoc = (name) => setSelectedMissingDocs((cur) => (cur.includes(name) ? cur.filter((n) => n !== name) : [...cur, name]));
  const askCustomerForSelected = () => {
    if (!selectedMissingDocs.length) return;
    openComposer({
      channel: "whatsapp", parties, defaultParty: parties[0], customerId: j.customerId,
      subjectHint: `[${j.id}] ${selectedMissingDocs.length} document${selectedMissingDocs.length > 1 ? "s" : ""} requested`,
      intent: `ask the customer to provide the following document(s) for job ${j.id}: ${selectedMissingDocs.join(", ")}`,
    });
    setSelectedMissingDocs([]);
  };
  // Item 10 — final Operations -> Finance handoff. Validation is already
  // computed server-side (GET .../finance-handoff-readiness, reusing the
  // same building blocks as GET /finance/ready-to-invoice); this just opens
  // the confirmation modal or, when blocked, no-ops (blockers already
  // render inline below, so the button itself is disabled in that case).
  const submitFinanceHandoff = async () => {
    setFinanceHandoffBusy(true);
    try {
      await api.post(`/operations/jobs/${j.id}/handoff-to-finance`, { expectedVersion: Number(j.version || 0) });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["job", j.id] }),
        qc.invalidateQueries({ queryKey: ["ops-finance-handoff", j.id] }),
        qc.invalidateQueries({ queryKey: ["ops-pipeline"] }),
        qc.invalidateQueries({ queryKey: ["rti"] }),
      ]);
      toast.success(`Job ${j.id} handed off to Finance for final invoicing`);
      setFinanceHandoffOpen(false);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not hand off this Job to Finance"));
    } finally { setFinanceHandoffBusy(false); }
  };
  const requestAdditionalTradeDocument = async () => {
    const name = extraDocName.trim();
    if (!name || !j.inquiryId) return;
    setExtraDocBusy(true);
    try {
      await api.post(`/commercial/inquiries/${j.inquiryId}/trade-documents`, { names: [name], required: true });
      await qc.invalidateQueries({ queryKey: ["inquiry-trade-docs", j.inquiryId] });
      setExtraDocName("");
      toast.success(`"${name}" added to the Trade Documents checklist`);
    } catch (err) { toast.error(apiErrorMessage(err, "Could not add document")); }
    finally { setExtraDocBusy(false); }
  };
  const restricted = j.costsRestricted;
  const gp = restricted ? null : j.estRevenue - (j.actualCost || j.estCost);

  return (
    <>
      <PageHeader
        title={<span className="flex items-center gap-2"><button onClick={() => navigate(backPath)} className="text-slate-400 hover:text-slate-700" data-testid="job-back"><ArrowLeft className="h-4 w-4" /></button>{j.id}</span>}
        subtitle={<div className="flex items-start gap-3 flex-wrap"><span>{j.customer} · {j.mode} · {j.scope}</span><BusinessStage stage={j.businessStage} /></div>}
        actions={<div className="flex items-center gap-1.5">
          {j.legacyRecord && <StatusPill status="Legacy / Verification Required" data-testid="job-legacy-badge" />}
          <StatusPill status={j.status} className="mr-1" />
          {canArchive && <Button size="sm" variant="outline" className="h-8" onClick={archiveJob} disabled={archiving} data-testid="job-archive">
            {archiving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Archive className="h-3.5 w-3.5 mr-1.5" />}
            {archiving ? "Archiving…" : "Archive job"}
          </Button>}
          <Button size="sm" variant="outline" className="h-8" onClick={() => openComposer({ channel: "call", parties, defaultParty: parties[0], customerId: j.customerId })} data-testid="job-call"><Phone className="h-3.5 w-3.5" /></Button>
          <Button size="sm" variant="outline" className="h-8" onClick={() => openComposer({ channel: "whatsapp", parties, defaultParty: parties[0], customerId: j.customerId })} data-testid="job-whatsapp"><MessageCircle className="h-3.5 w-3.5" /></Button>
          <Button size="sm" className="h-8 bg-indigo-600 hover:bg-indigo-700" onClick={() => openComposer({ channel: "email", parties, defaultParty: parties[0], customerId: j.customerId, subjectHint: `[${j.id}] ${j.mode} ${j.lane}`, intent: "send a proactive shipment status update" })} data-testid="job-email"><Mail className="h-3.5 w-3.5 mr-1.5" />Update customer</Button>
        </div>} />

      <div className="flex-1 overflow-y-auto">
        {/* Item 2 — customer-collected context (from messages/calls) at the
            top, above the fold, before route/milestone detail. */}
        {(comm?.aiSummary || comm?.intent) && (
          <div className="px-6 pt-5">
            <AICard title="What the customer has told us" footer={
              comm?.intent ? <span className="text-xs font-medium text-violet-700">Detected intent: {comm.intent}</span> : null
            }>
              {comm?.aiSummary || "No AI summary generated yet for this conversation."}
            </AICard>
          </div>
        )}
        <div className="px-6 pt-5">
          <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-6 flex-wrap">
            <ShipNode icon={MapPin} label="Origin" value={j.lane.split("→")[0]} />
            <div className="flex-1 min-w-[120px] flex items-center gap-2"><div className="h-0.5 flex-1 bg-gradient-to-r from-slate-200 to-indigo-300" /><Plane className="h-4 w-4 text-indigo-500" /><div className="h-0.5 flex-1 bg-gradient-to-r from-indigo-300 to-slate-200" /></div>
            <ShipNode icon={MapPin} label="Destination" value={j.lane.split("→")[1]} />
            <div className="border-l border-slate-200 pl-6 flex gap-6">
              <ShipNode icon={Package} label="Cargo" value={j.cargo} small />
              <div><div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Ref</div><div className="text-sm font-mono-num text-slate-700">{j.awb}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Incoterm</div><div className="text-sm font-medium text-slate-700">{j.incoterm}</div></div>
            </div>
          </div>
        </div>

        <div className="px-6 pt-3">
          <RouteTracker lane={j.lane} mode={j.mode} milestones={j.milestones} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 p-6">
          <div className="lg:col-span-2 space-y-4">
            {(j.exceptions || []).map((ex) => (
              <div key={ex.type} className="flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 p-3.5">
                <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                <div className="flex-1"><div className="flex items-center gap-2"><span className="font-semibold text-red-800 text-sm">{ex.type}</span><StatusPill status={ex.severity} bucket="danger" /></div><div className="text-[13px] text-red-700 mt-0.5">{ex.note}</div></div>
              </div>
            ))}

            {/* Bug fix: mounted once here (not inside a TabsContent) so the
                per-row "Upload" button on the Milestones tab and the "Upload
                trade document" button on the Documents tab can both trigger
                it regardless of which tab is currently active — Radix Tabs
                only mounts the active panel's content. */}
            {isOperations && <input ref={tradeDocConfirm.inputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={tradeDocConfirm.onFileChosen} data-testid="doc-upload-input" />}
            <Tabs defaultValue="milestones">
              <TabsList>
                <TabsTrigger value="milestones" data-testid="tab-milestones">Milestones</TabsTrigger>
                {isOperations && <TabsTrigger value="booking" data-testid="tab-booking">Booking &amp; Planning</TabsTrigger>}
                {isOperations && <TabsTrigger value="execution" data-testid="tab-execution">Execution &amp; POD</TabsTrigger>}
                {isOperations && <TabsTrigger value="customs" data-testid="tab-customs">Customs</TabsTrigger>}
                <TabsTrigger value="documents" data-testid="tab-documents">Documents ({(jobDocs || []).length})</TabsTrigger>
                <TabsTrigger value="parties" data-testid="tab-parties">Parties</TabsTrigger>
              </TabsList>

              <TabsContent value="milestones" className="mt-4">
                <div className="rounded-xl border border-slate-200 bg-white p-5">
                  <div className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Client journey · tracking</div>
                  <MilestoneTimeline milestones={j.milestones || []} />
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-5 mt-4" data-testid="required-docs">
                  <div className="flex items-center justify-between mb-2.5">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Trade documents · Document Name / Required / Submitted / Verification / Uploaded / Source</div>
                    {isOperations && selectedMissingDocs.length > 0 && (
                      <Button size="sm" className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700" onClick={askCustomerForSelected} data-testid="reqdoc-ask-selected-btn">
                        Ask Customer ({selectedMissingDocs.length})
                      </Button>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    {(reqDocs?.required || []).map((r) => {
                      const matched = (jobDocs || []).find((d) => {
                        const key = r.name.toLowerCase().split(" / ")[0];
                        const t = (d.type || "").toLowerCase();
                        return t === key || t.includes(key) || key.includes(t);
                      });
                      return (
                      <div key={r.name} data-testid={`reqdoc-${r.name.replace(/[^a-zA-Z]/g, "-")}`} className="rounded-lg border border-slate-100 px-2.5 py-2">
                        <div className="flex items-center justify-between text-sm gap-2">
                          <span className="flex items-center gap-2 text-slate-700 min-w-0">
                            {isOperations && !r.collected && (
                              <input type="checkbox" className="h-4 w-4 shrink-0" checked={selectedMissingDocs.includes(r.name)}
                                onChange={() => toggleMissingDoc(r.name)} data-testid={`reqdoc-select-${r.name.replace(/[^a-zA-Z]/g, "-")}`} />
                            )}
                            {r.collected ? <Check className="h-4 w-4 text-emerald-600 shrink-0" /> : <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />}
                            <span className="truncate">{r.name}</span>
                            <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5 shrink-0">Required</span>
                          </span>
                          <span className="shrink-0 flex items-center gap-1.5">
                            <span className={`text-[11px] font-medium ${r.collected ? "text-emerald-600" : "text-amber-600"}`}>{r.collected ? "Submitted" : "Pending"}</span>
                            {isOperations && !r.collected && (
                              <>
                                <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500" onClick={() => requestDocumentFromCustomer(r.name)} data-testid={`reqdoc-request-${r.name.replace(/[^a-zA-Z]/g, "-")}`}>Ask Customer</Button>
                                <Button size="sm" variant="ghost" className="h-7 text-xs text-indigo-600" onClick={() => { setUploadTargetDocName(r.name); tradeDocConfirm.openPicker(); }} data-testid={`reqdoc-submit-${r.name.replace(/[^a-zA-Z]/g, "-")}`}>Upload</Button>
                              </>
                            )}
                            {matched && <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500" onClick={() => openDoc(matched)} data-testid={`reqdoc-preview-${r.name.replace(/[^a-zA-Z]/g, "-")}`}>Preview</Button>}
                          </span>
                        </div>
                        {matched && <div className="text-[11px] text-slate-400 mt-0.5 ml-6">Verification: {matched.review || "Pending review"} · Uploaded {matched.date || "—"} · Source: {matched.source || "—"}</div>}
                      </div>
                      );
                    })}
                    {!(reqDocs?.required || []).length && <div className="text-[13px] text-slate-400">No document requirements defined for this job's service scope yet.</div>}
                  </div>
                  {/* Bug fix — the per-row "Upload" button above only stages
                      a file (tradeDocConfirm.pending); the actual submit
                      happens via this confirm bar. It previously only
                      existed on the separate Documents tab, so a file picked
                      from a row here (Milestones tab) had no visible way to
                      actually be submitted — Radix Tabs unmounts inactive
                      tabs, so the Documents tab's bar wasn't on screen. Now
                      also rendered here so wherever the picker was opened
                      from, the Submit step is immediately visible. */}
                  {isOperations && <FileSelectConfirmBar pending={tradeDocConfirm.pending} busy={tradeDocConfirm.busy} onSubmit={tradeDocConfirm.submit} onCancel={tradeDocConfirm.cancel} onReUpload={tradeDocConfirm.reUpload} submitLabel="Submit document" className="mt-3" />}
                  {/* Aug 2026 item 3 — Operations can flag a document beyond
                      this fixed job-level list as newly needed for the
                      Trade; it's added to the Trade's own inquiry-scoped
                      checklist (Sales sees it too on Customer360), not to
                      this job record. Only offered when the job is actually
                      linked back to an inquiry. */}
                  {isOperations && j.inquiryId && (
                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2" data-testid="reqdoc-add-additional">
                      <Input value={extraDocName} onChange={(e) => setExtraDocName(e.target.value)}
                        placeholder="Additional document needed for this trade…" className="h-8 text-xs flex-1"
                        data-testid="reqdoc-add-additional-input" />
                      <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!extraDocName.trim() || extraDocBusy}
                        onClick={requestAdditionalTradeDocument} data-testid="reqdoc-add-additional-btn">
                        {extraDocBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}Add to Trade Documents
                      </Button>
                    </div>
                  )}
                </div>
              </TabsContent>

              {isOperations && <TabsContent value="booking" className="mt-4" data-testid="booking-panel-tab">
                <OpsBookingPanel job={j} />
              </TabsContent>}

              {isOperations && <TabsContent value="execution" className="mt-4" data-testid="execution-panel-tab">
                <ExecutionPanel job={j} documents={jobDocs || []} />
              </TabsContent>}

              {isOperations && <TabsContent value="customs" className="mt-4" data-testid="customs-panel-tab">
                <CustomsPanel job={j} />
              </TabsContent>}

              <TabsContent value="documents" className="mt-4 space-y-5">
                {/* Item 8 — two structurally distinct sections: what the
                    CUSTOMER provided for the trade vs what OPERATIONS
                    uploads as evidence of actual cost. Never mixed. */}
                {(() => {
                  const docs = jobDocs || [];
                  const tradeDocs = docs.filter((d) => d.category !== "COST_EVIDENCE");
                  const costDocs = docs.filter((d) => d.category === "COST_EVIDENCE");
                  const DocRow = (d) => (
                    <button key={d.id} onClick={() => openDoc(d)} data-testid={`jobdoc-${d.id}`} className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors">
                      <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><FileText className="h-4 w-4 text-slate-500" /></div>
                      <div className="flex-1 min-w-0"><div className="text-sm font-medium text-slate-800">{d.name}</div><div className="text-[11px] text-slate-400">{d.type} · {d.source} · {d.date}</div></div>
                      {d.mismatch && <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
                      <StatusPill status={d.review} />
                    </button>
                  );
                  // Item 3 (Aug 2026 Operations round) — read-only KYC
                  // documents, sourced from the SAME checklist Sales/Finance
                  // already see (GET /customers/{cid}/document-checklist),
                  // never a second/duplicate KYC surface.
                  const kycRows = (custChecklist?.checklist || []).filter((r) => r.kind === "KYC");
                  return (
                    <>
                      {isOperations && kycRows.length > 0 && (
                        <div data-testid="kyc-documents-section">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><FileText className="h-3 w-3 text-slate-400" />Customer / KYC documents · collected by Sales, read-only here</span>
                          </div>
                          <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
                            {kycRows.map((r) => {
                              const matched = r.documentId ? docs.find((d) => d.id === r.documentId) : null;
                              return (
                                <div key={r.name} className="w-full px-4 py-3 flex items-center gap-3" data-testid={`kycdoc-${r.name.replace(/[^a-zA-Z]/g, "-")}`}>
                                  <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><FileText className="h-4 w-4 text-slate-500" /></div>
                                  <div className="flex-1 min-w-0"><div className="text-sm font-medium text-slate-800">{r.name}</div><div className="text-[11px] text-slate-400">{r.status === "Received" ? "Verified at onboarding / Sales KYC review" : "Not yet on file"}</div></div>
                                  <span className={`text-[11px] font-medium ${r.status === "Received" ? "text-emerald-600" : "text-amber-600"}`}>{r.status}</span>
                                  {matched && <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500" onClick={() => openDoc(matched)}>Preview</Button>}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <div data-testid="trade-documents-section">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><Sparkles className="h-3 w-3 text-violet-500" />Trade documents · from the customer</span>
                          {isOperations && (
                            <Button size="sm" variant="outline" className="h-8" disabled={tradeDocConfirm.busy} onClick={() => { setUploadTargetDocName(null); tradeDocConfirm.openPicker(); }} data-testid="doc-upload-btn">
                              {tradeDocConfirm.busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />}
                              {tradeDocConfirm.busy ? "Processing…" : "Upload trade document"}
                            </Button>
                          )}
                        </div>
                        {isOperations && <FileSelectConfirmBar pending={tradeDocConfirm.pending} busy={tradeDocConfirm.busy} onSubmit={tradeDocConfirm.submit} onCancel={tradeDocConfirm.cancel} onReUpload={tradeDocConfirm.reUpload} submitLabel="Submit document" className="mb-2" />}
                        <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
                          {tradeDocs.length ? tradeDocs.map(DocRow) : <div className="px-4 py-3 text-[13px] text-slate-400">No trade documents on file yet.</div>}
                        </div>
                      </div>

                      <div data-testid="cost-evidence-section">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><Truck className="h-3 w-3 text-indigo-500" />Cost & booking evidence · vendor invoices, booking confirmations</span>
                          {isOperations && <><input ref={costEvidenceConfirm.inputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={costEvidenceConfirm.onFileChosen} data-testid="cost-evidence-upload-input" />
                          <Button size="sm" variant="outline" className="h-8" disabled={costEvidenceConfirm.busy} onClick={costEvidenceConfirm.openPicker} data-testid="cost-evidence-upload-btn">
                            {costEvidenceConfirm.busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />}
                            {costEvidenceConfirm.busy ? "Processing…" : "Upload cost/booking evidence"}
                          </Button></>}
                        </div>
                        {/* Item 2 — the ONLY manual inputs left for actual cost:
                            which category this document is for, and the real
                            amount printed on it. Shown once a file is staged,
                            required before Submit is allowed to do anything. */}
                        {isOperations && costEvidenceConfirm.pending && (
                          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50/40 px-2.5 py-2 mb-2" data-testid="cost-evidence-fields">
                            <select value={costEvidenceCategory} onChange={(e) => setCostEvidenceCategory(e.target.value)}
                              className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs" data-testid="cost-evidence-category">
                              <option value="">Cost category…</option>
                              {OPS_COST_CATEGORIES.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                            </select>
                            <Input type="number" min="0" step="0.01" value={costEvidenceAmount} onChange={(e) => setCostEvidenceAmount(e.target.value)}
                              placeholder="Real amount on this document" className="h-8 text-xs w-52" data-testid="cost-evidence-amount" />
                          </div>
                        )}
                        {isOperations && <FileSelectConfirmBar pending={costEvidenceConfirm.pending} busy={costEvidenceConfirm.busy} onSubmit={costEvidenceConfirm.submit} onCancel={() => { costEvidenceConfirm.cancel(); setCostEvidenceCategory(""); setCostEvidenceAmount(""); }} onReUpload={costEvidenceConfirm.reUpload} submitLabel="Submit evidence" className="mb-2" />}
                        <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
                          {costDocs.length ? costDocs.map((d) => {
                            const inv = (actualCosts || []).find((s) => (s.documentIds || []).includes(d.id));
                            return (
                              <div key={d.id} className="w-full text-left px-4 py-3 flex items-center gap-3">
                                <button onClick={() => openDoc(d)} data-testid={`jobdoc-${d.id}`} className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition-opacity text-left">
                                  <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><FileText className="h-4 w-4 text-slate-500" /></div>
                                  <div className="flex-1 min-w-0"><div className="text-sm font-medium text-slate-800">{d.name}</div><div className="text-[11px] text-slate-400">{d.type} · {d.source} · {d.date}</div></div>
                                </button>
                                {inv && <span className="text-[11px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5 shrink-0" data-testid={`jobdoc-cost-tag-${d.id}`}>{inv.category || "Uncategorized"} · {money(inv.amount, inv.currency)}</span>}
                                <StatusPill status={d.review} />
                              </div>
                            );
                          }) : <div className="px-4 py-3 text-[13px] text-slate-400">No vendor cost/booking evidence uploaded yet.</div>}
                        </div>
                      </div>
                    </>
                  );
                })()}
              </TabsContent>

              <TabsContent value="parties" className="mt-4">
                <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
                  {(j.parties || []).map((p) => (
                    <div key={p.role} className="px-4 py-3 flex items-center justify-between">
                      <div><div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{p.role}</div><div className="text-sm font-medium text-slate-800">{p.name}</div><div className="text-[11px] text-slate-400 font-mono-num">{p.phone}</div></div>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openComposer({ channel: "call", parties, defaultParty: `${p.role} · ${p.name}`, customerId: j.customerId })}><Phone className="h-3.5 w-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openComposer({ channel: "whatsapp", parties, defaultParty: `${p.role} · ${p.name}`, customerId: j.customerId })}><MessageCircle className="h-3.5 w-3.5" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>

          <div className="space-y-4">
            {isOperations ? <AICard title="Next action" footer={nextAction?.aiGenerated === false ? (
                <span className="text-xs text-slate-400 italic">AI recommendation unavailable — showing the current required action</span>
              ) : undefined}>
              <div className="flex items-center gap-1.5">
                <span className="font-semibold text-slate-800">{nextAction?.what || "Review job"}</span>
                {nextAction?.aiGenerated && <AIBadge>AI</AIBadge>}
              </div>
              {nextAction?.why && <div className="mt-1 text-xs text-slate-600">{nextAction.why}</div>}
              {nextAction?.whenDue && <div className="mt-2 text-[11px] text-slate-500">Due: {nextAction.whenDue}</div>}
            </AICard> : <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600" data-testid="job-read-only"><div className="font-semibold text-slate-800">Read-only operational view</div><div className="mt-1 text-xs">Booking, execution and document collection are maintained by Operations.</div></div>}

            {restricted ? (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 flex items-center gap-2.5 text-sm text-slate-500"><Lock className="h-4 w-4 shrink-0" />Job cost & margin are restricted for your role.</div>
            ) : (
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-3">Job financials</div>
                <div className="space-y-2.5 text-sm">
                  <Row label="Est. revenue" value={money(j.estRevenue)} />
                  <Row label="Est. cost" value={money(j.estCost)} muted />
                  <Row label="Actual cost (so far)" value={costReadiness?.grandTotal ? money(costReadiness.grandTotal, costReadiness.currency) : (j.actualCost ? money(j.actualCost) : "—")} muted />
                  <div className="border-t border-slate-100 pt-2 flex items-center justify-between"><span className="text-slate-500">Projected GP</span><span className="font-mono-num font-bold text-emerald-600">{money(gp)}</span></div>
                </div>
                <div className="mt-3 text-[11px] text-slate-400">
                  {/* Item 2 — actual cost is derived automatically from real
                      cost documents already uploaded/categorized above; there
                      is no manual actual-cost entry left on this Job. */}
                  Actual cost is derived from categorized cost documents (above) — never typed in directly.
                </div>
                {isOperations && costReadiness && (
                  <div className="mt-3" data-testid="ops-cost-handoff-card">
                    {costReadiness.alreadyHandedOff ? (
                      <div className="text-[13px] text-emerald-700 flex items-center gap-1.5"><Check className="h-4 w-4" />Sent to Finance {new Date(costReadiness.handoffAt).toLocaleString()} by {costReadiness.handoffBy}</div>
                    ) : (
                      <>
                        {!costReadiness.ready && (
                          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2" data-testid="ops-cost-handoff-blocker">
                            Missing Cost Documents: {costReadiness.missing.join(", ")}
                          </div>
                        )}
                        <Button size="sm" variant="outline" className="h-8 w-full" disabled={!costReadiness.ready}
                          onClick={() => setCostHandoffOpen(true)} data-testid="ops-submit-actual-cost-btn">
                          Send actual costs to Finance
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {isOperations && handoffReadiness && (
              <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ops-finance-handoff-card">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Final handoff to Finance</div>
                {handoffReadiness.alreadyHandedOff ? (
                  <div className="text-[13px] text-emerald-700 flex items-center gap-1.5"><Check className="h-4 w-4" />Sent to Finance {new Date(handoffReadiness.handoffAt).toLocaleString()} by {handoffReadiness.handoffBy}</div>
                ) : (
                  <>
                    {!handoffReadiness.ready && (
                      <div className="space-y-1 mb-2" data-testid="ops-finance-handoff-blockers">
                        {handoffReadiness.blockers.map((b, i) => (
                          <div key={i} className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">{b}</div>
                        ))}
                      </div>
                    )}
                    <Button size="sm" className="h-8 w-full bg-emerald-600 hover:bg-emerald-700" disabled={!handoffReadiness.ready}
                      onClick={() => setFinanceHandoffOpen(true)} data-testid="ops-send-to-finance-btn">
                      Send to Finance for final invoice
                    </Button>
                  </>
                )}
              </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Origin (from Sales handoff)</div>
              <div className="space-y-1.5 text-[13px] text-slate-600">
                <div>Quotation <span className="font-mono-num text-slate-800">{j.quotationId || "—"}</span></div>
                <div>Shipper: {j.shipper}</div>
                <div>Consignee: {j.consignee}</div>
                <div className="pt-1"><StatusPill status={j.creditStatus} /> <span className="text-slate-400 ml-1">by Finance</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <DocumentPanel doc={activeDoc} open={docOpen} onOpenChange={setDocOpen} />

      {/* Item 2 — review-ONLY: every row is server-aggregated from real,
          already-created supplier_invoices records (one per uploaded +
          categorized cost document). No editable fields — this dialog can
          only confirm what's already true, never invent a number. */}
      {costReadiness && (
        <HandoffConfirmDialog
          open={costHandoffOpen}
          onOpenChange={setCostHandoffOpen}
          busy={costHandoffBusy}
          title="Hand off actual costs to Finance"
          confirmLabel="Confirm and send to Finance"
          summaryRows={[
            { label: "Job", value: j.id },
            { label: "Customer", value: j.customer },
            ...costReadiness.byCategory.filter((c) => c.count > 0).map((c) => ({ label: c.category, value: `${money(c.total, c.currency)} (${c.count} doc${c.count > 1 ? "s" : ""})` })),
            { label: "Cost documents", value: `${costReadiness.byCategory.filter((c) => c.count > 0).length}/${costReadiness.requiredCategories.length} categories available`, wide: true },
            { label: "Grand total actual cost", value: money(costReadiness.grandTotal, costReadiness.currency), wide: true },
          ]}
          receivingTeamSees={[
            "Every real supplier-invoice cost record already created from Operations' categorized cost-document uploads, in the Supplier Invoices (AP) queue.",
            "Any category with a Match/Variance flag Finance's Costs & Variance review already computes per record.",
            "Every trade document and cost/booking evidence document already on this job, for full context.",
          ]}
          onConfirm={submitCostHandoff}
        />
      )}

      {/* Item 10 — final Operations -> Finance handoff, double-confirmed the
          same way as item 7's actual-cost handoff above: a summary modal
          before anything is sent, never immediate on first click. */}
      {handoffReadiness && (
        <HandoffConfirmDialog
          open={financeHandoffOpen}
          onOpenChange={setFinanceHandoffOpen}
          busy={financeHandoffBusy}
          title="Send this Job to Finance for final invoicing"
          confirmLabel="Confirm and send to Finance"
          summaryRows={[
            { label: "Job", value: j.id },
            { label: "Customer", value: j.customer },
            { label: "Operational status", value: handoffReadiness.operationalStatus },
            { label: "Cost documents", value: `${handoffReadiness.costDocuments.completed}/${handoffReadiness.costDocuments.total} uploaded` },
            { label: "POD", value: handoffReadiness.podStatus },
            { label: "Actual cost", value: money(handoffReadiness.actualCost, handoffReadiness.currency) },
          ]}
          receivingTeamSees={[
            "This case in the Finance Pipeline's Invoice Clearance stage, ready for final invoice preparation.",
            "Every uploaded cost/booking evidence document and trade document already on this Job.",
            "The verified POD and actual cost data — Finance prepares and issues the final invoice; Operations is never responsible for collecting customer payment.",
          ]}
          onConfirm={submitFinanceHandoff}
        />
      )}
    </>
  );
}

const ShipNode = ({ icon: Icon, label, value, small }) => <div className={small ? "max-w-[180px]" : ""}><div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold"><Icon className="h-3 w-3" />{label}</div><div className={`font-medium text-slate-800 ${small ? "text-[12px]" : "text-sm"}`}>{value}</div></div>;
const Row = ({ label, value, muted }) => <div className="flex items-center justify-between"><span className="text-slate-400">{label}</span><span className={`font-mono-num font-medium ${muted ? "text-slate-500" : "text-slate-800"}`}>{value}</span></div>;
